'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import config from '@haaretz/l-config';
import isStorybook from '@haaretz/s-common-utils/isStorybook';
import { lgnPathnameStages } from '@haaretz/s-consts';
import getSafeRedirectUrl from '@haaretz/s-url-utils/getSafeRedirectUrl';
import { usePathname, useSearchParams } from 'next/navigation';
import React from 'react';
export const LoginContext = /*#__PURE__*/ React.createContext(null);
export const LoginActionsContext = /*#__PURE__*/ React.createContext(null);
export function useLoginApp() {
    const context = React.use(LoginContext);
    if (!context) {
        if (isStorybook()) {
            // NOTE: Default values for Storybook environment in PP stories
            return {
                stage: 'emailEntry',
                email: '',
                loweredCaseSpacedStage: 'email entry',
                returnTo: null,
                isLoading: false,
                isLoadingDelayed: false
            };
        }
        throw new Error('useLoginApp must be used within a LoginContext');
    }
    return context;
}
export function useLoginAppActions() {
    const context = React.use(LoginActionsContext);
    if (!context) {
        throw new Error('useLoginAppActions must be used within a LoginContext');
    }
    return context;
}
const defaultStage = 'emailEntry';
const homepageUrl = config.get('homepageUrl');
export default function LoginProvider({ children }) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const returnToFromUrl = searchParams.get('returnTo');
    const [returnTo, setReturnTo] = React.useState(null);
    const stageFromPathname = lgnPathnameStages.find((stage)=>pathname.includes(stage));
    const [stage, setStage] = React.useState(searchParams.has('spotify') ? 'loginSpotify' : defaultStage);
    const [email, setEmail] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingDelayed] = React.useState(false);
    const loweredCaseSpacedStage = getLowerCaseSpacedString(stageFromPathname && stage === defaultStage ? stageFromPathname : stage);
    React.useEffect(()=>{
        if (!returnTo) {
            const safeReturnToUrl = getSafeRedirectUrl(returnToFromUrl);
            const safeReferrer = getSafeRedirectUrl(document.referrer);
            setReturnTo(safeReturnToUrl || safeReferrer || homepageUrl);
        }
    }, [
        returnTo,
        returnToFromUrl
    ]);
    const value = React.useMemo(()=>({
            returnTo,
            stage,
            email,
            isLoading,
            isLoadingDelayed,
            loweredCaseSpacedStage
        }), [
        returnTo,
        stage,
        email,
        isLoading,
        isLoadingDelayed,
        loweredCaseSpacedStage
    ]);
    const actions = React.useMemo(()=>({
            setReturnTo,
            setStage,
            setEmail,
            setIsLoading
        }), [
        setReturnTo,
        setStage,
        setEmail,
        setIsLoading
    ]);
    return /*#__PURE__*/ _jsx(LoginContext, {
        value: value,
        children: /*#__PURE__*/ _jsx(LoginActionsContext, {
            value: actions,
            children: children
        })
    });
}
function getLowerCaseSpacedString(camelCase) {
    return camelCase.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

//# sourceMappingURL=LoginProvider.js.map